import { initializeApp } from 'firebase/app';
import { initializeAnalytics, isSupported, logEvent, setUserId, setUserProperties, type Analytics } from 'firebase/analytics';
import { ApiError } from './api';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const firebaseApp = initializeApp(firebaseConfig);
const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
let analyticsPromise: Promise<Analytics | null> | null = null;
const ANALYTICS_STATUS_KEY = 'munshi_web_analytics_status';

export const webAnalyticsEvents = {
  loadSheddingOpened: 'load_shedding_opened',
  loadSheddingModeSelected: 'load_shedding_mode_selected',
  loadSheddingProviderSelected: 'load_shedding_provider_selected',
  loadSheddingFeederSelected: 'load_shedding_feeder_selected',
  loadSheddingValidationFailed: 'load_shedding_validation_failed',
  loadSheddingSearchStarted: 'load_shedding_search_started',
  loadSheddingSearchCompleted: 'load_shedding_search_completed',
  loadSheddingSearchNotFound: 'load_shedding_search_not_found',
  loadSheddingSearchFailed: 'load_shedding_search_failed',
  loadSheddingAreaReportOpened: 'load_shedding_area_report_opened',
  loadSheddingAreaReportStarted: 'load_shedding_area_report_started',
  loadSheddingAreaReportSubmitted: 'load_shedding_area_report_submitted',
  loadSheddingAreaReportFailed: 'load_shedding_area_report_failed',
  loadSheddingAlertAuthRequired: 'load_shedding_alert_auth_required',
  loadSheddingAlertStarted: 'load_shedding_alert_started',
  loadSheddingAlertEnabled: 'load_shedding_alert_enabled',
  loadSheddingAlertFailed: 'load_shedding_alert_failed',
  loadSheddingCommunityReportAuthRequired: 'load_shedding_community_auth_required',
  loadSheddingCommunityReportStarted: 'load_shedding_community_report_started',
  loadSheddingCommunityReportSubmitted: 'load_shedding_community_report_submitted',
  loadSheddingCommunityReportFailed: 'load_shedding_community_report_failed',
} as const;

function setAnalyticsStatus(status: 'initializing' | 'ready' | 'unsupported' | 'error') {
  try { sessionStorage.setItem(ANALYTICS_STATUS_KEY, status); } catch { /* Storage may be unavailable. */ }
}

export type RequestFailureCategory =
  | 'invalid_input'
  | 'bill_not_found'
  | 'authentication_required'
  | 'permission_denied'
  | 'request_cancelled'
  | 'rate_limited'
  | 'timeout'
  | 'provider_unavailable'
  | 'server_error'
  | 'network_error'
  | 'request_error';

export function safeRequestFailureParameters(error: unknown, requestStage: string, retryAttempt = 0) {
  const status = error instanceof ApiError ? error.status : 0;
  let failureCategory: RequestFailureCategory = 'request_error';

  if (error instanceof DOMException && error.name === 'AbortError') failureCategory = 'request_cancelled';
  else if (error instanceof TypeError) failureCategory = 'network_error';
  else if (status === 400 || status === 422) failureCategory = 'invalid_input';
  else if (status === 404) failureCategory = 'bill_not_found';
  else if (status === 401) failureCategory = 'authentication_required';
  else if (status === 403) failureCategory = 'permission_denied';
  else if (status === 408 || status === 504) failureCategory = 'timeout';
  else if (status === 429) failureCategory = 'rate_limited';
  else if (status === 502 || status === 503) failureCategory = 'provider_unavailable';
  else if (status >= 500) failureCategory = 'server_error';

  return {
    failure_category: failureCategory,
    http_status: status,
    request_stage: requestStage,
    retry_attempt: Math.max(0, retryAttempt),
    browser_connection: typeof navigator === 'undefined' ? 'unknown' : navigator.onLine ? 'online' : 'offline',
    app_version: appVersion,
  };
}

function analyticsClient() {
  if (!analyticsPromise) {
    setAnalyticsStatus('initializing');
    analyticsPromise = isSupported()
      // React Router emits the canonical page_view below. Disable Firebase's
      // automatic first page view so the landing route is not counted twice.
      .then(supported => {
        if (!supported) {
          setAnalyticsStatus('unsupported');
          return null;
        }
        const analytics = initializeAnalytics(firebaseApp, { config: { send_page_view: false } });
        setAnalyticsStatus('ready');
        return analytics;
      })
      .catch(error => {
        setAnalyticsStatus('error');
        console.warn('Firebase Analytics is unavailable', error);
        return null;
      });
  }
  return analyticsPromise;
}

const productSources: Record<string, string> = {
  'bill-analyzer': 'bill',
  'bill-so-far': 'bill_so_far',
  'load-understanding': 'appliances',
  'load-shedding': 'load_shedding',
  'smart-solar': 'solar',
  'solar-market': 'solar',
  'solar-finance': 'solar',
  'saving-game': 'saving_game',
  notifications: 'notifications',
};

export function productSourceForPath(pathname: string) {
  const route = pathname.split('/').filter(Boolean)[0] || 'home';
  return productSources[route] || route;
}

function accountState() {
  try { return localStorage.getItem('munshi_web_user') ? 'authenticated' : 'guest'; }
  catch { return 'guest'; }
}

export async function trackPageView(pathname: string, language: 'en' | 'ur', pageTitle: string) {
  const analytics = await analyticsClient();
  if (!analytics) return;
  // Never send query parameters. Some app routes carry electricity references,
  // vendor IDs, or other user-entered values in the URL.
  const pagePath = `/app${pathname}`;
  logEvent(analytics, 'page_view', {
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
    page_title: pageTitle,
    site_surface: 'web_app',
    product_source: productSourceForPath(pathname),
    interface_language: language,
    account_state: accountState(),
    app_version: appVersion,
  });
}

export async function trackWebEvent(name: string, parameters: Record<string, string | number | boolean | null | undefined> = {}) {
  if (!/^[a-z][a-z0-9_]{0,39}$/.test(name)) {
    console.warn(`Analytics event name is invalid and was not sent: ${name}`);
    return;
  }
  const analytics = await analyticsClient();
  if (!analytics) return;
  const pathname = window.location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
  const language = localStorage.getItem('munshi_web_language') === 'ur' ? 'ur' : 'en';
  const safeParameters = Object.fromEntries(
    Object.entries(parameters)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => [key, typeof value === 'string' ? value.slice(0, 100) : value]),
  );
  logEvent(analytics, name, {
    site_surface: 'web_app',
    product_source: productSourceForPath(pathname),
    interface_language: language,
    page_path: `/app${pathname}`,
    account_state: accountState(),
    app_version: appVersion,
    ...(import.meta.env.DEV ? { debug_mode: true } : {}),
    ...safeParameters,
  });
}

export async function identifyWebUser(userId: number | string | null, language: 'en' | 'ur') {
  const analytics = await analyticsClient();
  if (!analytics) return;
  setUserId(analytics, userId === null ? null : String(userId));
  setUserProperties(analytics, { site_surface: 'web_app', preferred_language: language });
}
